import { useChat } from '@ai-sdk/react';
import { Ionicons } from '@expo/vector-icons';
import { DefaultChatTransport } from 'ai';
import { fetch as expoFetch } from 'expo/fetch';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Dimensions,
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import type { UIMessage } from 'ai';

import { CalendarEventModal } from '@/components/calendar/event-modal';
import { contentReportManager } from '@/lib/managers/ContentReportManager';
import { reportContextStore } from '@/lib/report/ReportContextStore';
import { BorderRadius, Spacing } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { useChatEventModal } from '@/hooks/use-chat-event-modal';
import { useResolvedDefaultCalendarId } from '@/hooks/use-resolved-default-calendar-id';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useKeyboardHeight } from '@/hooks/use-keyboard-height';
import { usePendingChatAction } from '@/hooks/use-pending-chat-action';
import { useVoiceRecording } from '@/hooks/use-voice-recording';
import { useChatStore } from '@/lib/chat/useChatStore';
import { ChatTaskCreator } from '@/lib/chat/ChatTaskCreator';
import { stripImagesFromHistory } from '@/lib/chat/stripImagesFromHistory';
import { FamilyContextCollector, type CollectedFamilyContext } from '@/lib/family/FamilyContextCollector';
import { useFamilyMembersStore, useCalendarMemberMappingStore } from '@/hooks/use-scoped-managers';
import { useChatAttachment } from '@/hooks/use-chat-attachment';
import { useSupabaseAuth } from '@/lib/supabase';
import { supabase } from '@/lib/supabase/client';
import { LIMITS_BY_TIER } from '@/types/ai-limits';
import type { VoiceActionItem } from '@/types/voice-transcription';
import { generateAPIUrl } from '@/utils';
import { ChatAttachmentButton } from './ChatAttachmentButton';
import { ChatAttachmentPreview } from './ChatAttachmentPreview';
import { ChatConversationView } from './ChatConversationView';
import { ChatInputBar, ChatInputBarRef } from './ChatInputBar';
import { ChatTopBar } from './ChatTopBar';
import { ChatTopBlur } from './ChatTopBlur';
import { VoiceRecordingButton } from './VoiceRecordingButton';
import { VoiceRecordingOverlay } from './VoiceRecordingOverlay';

const SCREEN_HEIGHT = Dimensions.get('window').height;
const TOP_BAR_HEIGHT = 46; // button size + padding

interface ChatModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export function ChatModal({ visible, onDismiss }: ChatModalProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { session } = useSupabaseAuth();

  // Global chat store
  const storeMessages = useChatStore((state) => state.messages);
  const setStoreMessages = useChatStore((state) => state.setMessages);
  const resetSession = useChatStore((state) => state.resetSession);
  const incrementTaskRefreshSignal = useChatStore((state) => state.incrementTaskRefreshSignal);
  const requestTodoNavigation = useChatStore((state) => state.requestTodoNavigation);
  const closeModal = useChatStore((state) => state.closeModal);

  // Event modal for "Add event" action
  const eventModal = useChatEventModal();
  const storedDefaultCalendarId = useResolvedDefaultCalendarId(eventModal.writableCalendars);

  // Voice recording
  const {
    recordingState, transcribedText, error: voiceError, startRecording, stopRecording, clearTranscription,
  } = useVoiceRecording();

  // Picture attachment
  const attachment = useChatAttachment();

  const { keyboardHeight } = useKeyboardHeight();
  const inputRef = useRef<ChatInputBarRef>(null);

  // Store latest session in ref for access in customFetch
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Collect family context once (and on store changes); ref so customFetch reads it without
  // re-collecting on every keystroke.
  const familyMembersStore = useFamilyMembersStore();
  const calendarMappingStore = useCalendarMemberMappingStore();
  const familyContextRef = useRef<CollectedFamilyContext | null>(null);
  useEffect(() => {
    let cancelled = false;
    FamilyContextCollector.collect(familyMembersStore, calendarMappingStore)
      .then((ctx) => {
        if (!cancelled) familyContextRef.current = ctx;
      })
      .catch((err) => {
        console.warn('[ai-chat] family context collection failed:', err);
        if (!cancelled) familyContextRef.current = null;
      });
    return () => { cancelled = true; };
  }, [familyMembersStore, calendarMappingStore]);

  // Store latest attachment onSendSucceeded for use in useChat onFinish
  const onSendSucceededRef = useRef(attachment.onSendSucceeded);
  useEffect(() => { onSendSucceededRef.current = attachment.onSendSucceeded; }, [attachment.onSendSucceeded]);

  const transport = useMemo(() => {
    const customFetch: typeof globalThis.fetch = async (input, init) => {
      const { data: sessionData } = await supabase.auth.getSession();
      let token = sessionData.session?.access_token;

      if (!token) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        token = refreshed.session?.access_token ?? undefined;
      }

      if (!token) {
        await supabase.auth.signOut().catch(() => {});
        throw new Error('Session expired');
      }

      init = { ...init, headers: { ...init?.headers, Authorization: `Bearer ${token}` } };

      if (init.body) {
        try {
          const body = JSON.parse(init.body as string);
          body.timezoneOffset = new Date().getTimezoneOffset();
          if (Array.isArray(body.messages)) {
            body.messages = stripImagesFromHistory(body.messages);
          }
          const fctx = familyContextRef.current;
          if (fctx) {
            body.familyMembers = fctx.familyMembers;
            body.familyMemberNotes = fctx.familyMemberNotes;
            body.meFamilyMemberId = fctx.meFamilyMemberId;
          }
          init.body = JSON.stringify(body);
        } catch (error) {
          console.error('[chat] Failed to prepare request body:', error);
        }
      }
      return expoFetch(input as string, init as any) as Promise<Response>;
    };

    return new DefaultChatTransport({ fetch: customFetch, api: generateAPIUrl('/api/chat') });
  }, []);  

  const {
    messages, status, error, sendMessage: sendChatMessage, stop, setMessages,
  } = useChat({
    transport,
    onError: useCallback((err: Error) => console.error('[ai-chat]', err), []),
    onFinish: useCallback(() => {
      onSendSucceededRef.current?.().catch((err) => console.error('[ai-chat:purge]', err));
    }, []),
  });

  // Initialize useChat with messages from global store on mount
  useEffect(() => {
    if (storeMessages.length > 0 && messages.length === 0) setMessages(storeMessages);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync messages from useChat to global store
  useEffect(() => {
    if (messages.length > 0 || storeMessages.length > 0) setStoreMessages(messages);
  }, [messages, setStoreMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  const [input, setInput] = useState('');
  const [usageLimitError, setUsageLimitError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  // Pending confirmation actions (accept/reject)
  const {
    pendingAction, isExecuting, handleAccept, handleReject, clearPendingAction,
  } = usePendingChatAction(messages, sendChatMessage, setUsageLimitError);

  const userName = session?.user?.user_metadata?.full_name
    || session?.user?.email?.split('@')[0] || 'User';

  const isStreaming = status === 'streaming' || (status as string) === 'loading';
  const isLoading = isStreaming || isSending;

  useEffect(() => {
    if (isStreaming && isSending) setIsSending(false);
  }, [isStreaming, isSending]);

  // Shared wrapper: usage-limit detection + sending state for any send path.
  const performSend = useCallback(
    async (send: () => Promise<void>) => {
      setUsageLimitError(null);
      setIsSending(true);
      try {
        await send();
      } catch (err: any) {
        if (err?.message?.includes('limit') || err?.message?.includes('429')) {
          setUsageLimitError(err.message);
        } else {
          console.error('[ai-chat:send]', err);
        }
      } finally {
        setIsSending(false);
      }
    },
    []
  );

  const handleSend = useCallback(async () => {
    const trimmedText = input.trim();
    const hasAttachment = attachment.status === 'ready';
    if (!trimmedText && !hasAttachment) return;
    if (isLoading) return;

    setInput('');

    await performSend(async () => {
      if (hasAttachment) {
        const dataUrl = await attachment.buildSendDataUrl();
        if (!dataUrl) {
          throw new Error('Could not read photo — try picking it again.');
        }
        const parts: any[] = [];
        if (trimmedText) parts.push({ type: 'text', text: trimmedText });
        parts.push({ type: 'file', mediaType: 'image/jpeg', url: dataUrl });
        await sendChatMessage({ parts });
      } else {
        await sendChatMessage({ text: trimmedText });
      }
    });
  }, [input, isLoading, sendChatMessage, attachment, performSend]);

  const handleStarterPrompt = useCallback(
    async (prompt: string) => {
      if (isLoading) return;
      await performSend(() => sendChatMessage({ text: prompt }));
    },
    [isLoading, sendChatMessage, performSend]
  );

  const handleNewChat = useCallback(() => {
    stop();
    setMessages([]);
    resetSession();
    setUsageLimitError(null);
    clearPendingAction();
    attachment.clear().catch((err) => console.error('[ai-chat:clear-attachment]', err));
  }, [setMessages, stop, resetSession, clearPendingAction, attachment]);

  const handleClose = useCallback(() => { onDismiss(); }, [onDismiss]);

  // Typewriter animation: populate input field character by character
  const typewriterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startTypewriter = useCallback((text: string) => {
    if (typewriterTimerRef.current) {
      clearTimeout(typewriterTimerRef.current);
      typewriterTimerRef.current = null;
    }
    setInput('');

    const scheduleChar = (i: number) => {
      if (i > text.length) return;
      typewriterTimerRef.current = setTimeout(() => {
        setInput(text.slice(0, i));
        scheduleChar(i + 1);
      }, 5);
    };
    scheduleChar(1);
  }, []);

  useEffect(() => {
    if (!transcribedText) return;
    const text = transcribedText;
    clearTranscription();
    startTypewriter(text);
  }, [transcribedText]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup typewriter on unmount
  useEffect(() => {
    return () => {
      if (typewriterTimerRef.current) clearTimeout(typewriterTimerRef.current);
    };
  }, []);

  // Show voice recording/transcription errors to the user
  useEffect(() => {
    if (voiceError) {
      Alert.alert('Voice Error', voiceError);
    }
  }, [voiceError]);

  const handleVoicePress = useCallback(() => {
    if (recordingState === 'recording') {
      stopRecording();
    } else {
      startRecording();
    }
  }, [recordingState, startRecording, stopRecording]);

  const handleVoiceActionAddTask = useCallback(async (item: VoiceActionItem) => {
    const userId = session?.user?.id;
    if (!userId) {
      console.warn('[ChatModal] Cannot add task: user not signed in');
      return;
    }
    try {
      if (item.existingTaskId && item.subtasks?.length) {
        await ChatTaskCreator.addSubtasksToTask(item.existingTaskId, item.subtasks);
      } else {
        await ChatTaskCreator.createTask(item);
      }
      incrementTaskRefreshSignal();
      requestTodoNavigation();
      closeModal();
    } catch (err) {
      console.error('[ChatModal] Failed to add task:', err);
    }
  }, [session, incrementTaskRefreshSignal, requestTodoNavigation, closeModal]);

  const handleVoiceActionAddEvent = useCallback((item: VoiceActionItem) => {
    eventModal.openEventModal(item);
  }, [eventModal]);

  const handleReportMessage = useCallback((message: UIMessage) => {
    reportContextStore.set(contentReportManager.buildChatContext(message));
    onDismiss();
    router.push('/report-ai-content');
  }, [onDismiss, router]);

  useEffect(() => {
    if (visible) inputRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  // Height of the top blur zone: status bar + top bar + fade margin
  const topBlurHeight = insets.top + TOP_BAR_HEIGHT + 20;

  return (
    <Modal visible={visible} transparent animationType="none"
      onRequestClose={handleClose} statusBarTranslucent>
      <View style={styles.modalOverlay}>
        <View style={styles.backdrop}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={handleClose} />
        </View>

        <VoiceRecordingOverlay
          visible={recordingState === 'recording'}
          accentColor={palette.primary}
          onStop={stopRecording}
        />

        {/* Top blur gradient — anchored to screen top */}
        <ChatTopBlur height={topBlurHeight} />

        {/* Top-right buttons — anchored to screen top */}
        <ChatTopBar
          topInset={insets.top}
          onNewChat={handleNewChat}
          onClose={handleClose}
        />

        <View style={[styles.modalContent, {
          backgroundColor: palette.chatOverlayScrim, overflow: 'hidden',
          height: SCREEN_HEIGHT, bottom: keyboardHeight > 0 ? keyboardHeight + 8 : 0,
        }]}>
          <View style={styles.flex}>
            <ChatConversationView
              messages={messages} accentColor={palette.tint} textColor={palette.text}
              userName={userName} error={error}
              usageLimitError={usageLimitError}
              topInset={topBlurHeight}
              onVoiceActionAddTask={handleVoiceActionAddTask}
              onVoiceActionAddEvent={handleVoiceActionAddEvent}
              onReport={handleReportMessage}
              onStarterPromptPress={handleStarterPrompt}
            />

            <View style={[styles.inputContainer, { paddingBottom: Math.max(insets.bottom, 8) }]}>
              {isLoading && (
                <ActivityIndicator size="small" color={palette.textMuted} style={styles.loadingIndicator} />
              )}
              {pendingAction && (
                <View style={styles.pendingActions}>
                  <TouchableOpacity onPress={handleAccept}
                    style={[styles.actionButton, { backgroundColor: palette.success }]}
                    disabled={isExecuting} accessibilityLabel="Accept action">
                    <Ionicons name="checkmark" size={18} color="#FFFFFF" />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={handleReject}
                    style={[styles.actionButton, { backgroundColor: palette.danger }]}
                    disabled={isExecuting} accessibilityLabel="Reject action">
                    <Ionicons name="close" size={18} color="#FFFFFF" />
                  </TouchableOpacity>
                </View>
              )}

              <ChatInputBar ref={inputRef} input={input}
                isLoading={isLoading || !!usageLimitError}
                accentColor={palette.tint} backgroundColor={palette.backgroundLight}
                textColor={palette.text} showDisclaimer={false}
                onChangeText={setInput} onSubmit={handleSend}
                allowEmptySubmit={attachment.status === 'ready'}
                placeholder={
                  usageLimitError ? 'Usage limit reached'
                    : recordingState === 'transcribing' ? 'Transcribing...'
                      : attachment.status === 'compressing' ? 'Preparing photo...'
                        : attachment.status === 'ready' ? 'Add a note or hit send'
                          : 'Ask AI to organise something...'
                }
                maxLength={LIMITS_BY_TIER.free.inputCharacterLimit}
                attachmentButton={
                  recordingState === 'idle' && attachment.status !== 'picking' ? (
                    <ChatAttachmentButton
                      disabled={isLoading || attachment.status === 'compressing'}
                      accentColor={palette.primary}
                      onPickFromCamera={attachment.attachFromCamera}
                      onPickFromLibrary={attachment.attachFromLibrary}
                    />
                  ) : null
                }
                attachmentPreview={
                  <ChatAttachmentPreview
                    imageUri={attachment.displayDataUrl}
                    isCompressing={attachment.status === 'compressing'}
                    onClear={() => {
                      attachment.clear().catch((err) => console.error('[ai-chat:clear]', err));
                    }}
                  />
                }
                voiceButton={
                  attachment.status === 'idle' ? (
                    <VoiceRecordingButton recordingState={recordingState}
                      accentColor={palette.primary}
                      onPress={handleVoicePress} />
                  ) : null
                }
              />
            </View>
          </View>
        </View>
      </View>

      {eventModal.showEventModal && (
        <CalendarEventModal
          visible={true}
          mode="create"
          defaultTitle={eventModal.eventActionItem?.title}
          defaultDescription={eventModal.eventActionItem?.description}
          preselectedDate={eventModal.eventActionItem?.date ?? null}
          preselectedTime={eventModal.eventActionItem?.time ?? null}
          writableCalendars={eventModal.writableCalendars}
          allCalendars={eventModal.allCalendars}
          defaultCalendarId={storedDefaultCalendarId ?? eventModal.writableCalendars[0]?.id ?? null}
          familyMembers={eventModal.familyMembers}
          calendarMappings={eventModal.calendarMappings}
          onSave={eventModal.handleEventSave}
          onDismiss={eventModal.handleEventDismiss}
          isSubmitting={eventModal.isSubmittingEvent}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalOverlay: { flex: 1 },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'transparent' },
  modalContent: { position: 'absolute', bottom: 0, left: 0, right: 0 },
  flex: { flex: 1 },
  inputContainer: {
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.sm,
    gap: Spacing.sm,
    borderTopLeftRadius: BorderRadius.lg,
    borderTopRightRadius: BorderRadius.lg,
  },
  loadingIndicator: {
    alignSelf: 'flex-start',
  },
  pendingActions: {
    flexDirection: 'row',
    gap: Spacing.sm,
    justifyContent: 'flex-end',
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
